import type { KeyboardEvent } from 'react'

/**
 * Makes a `<div>` row operable by keyboard as well as by mouse.
 *
 * A bare `<div onClick>` is invisible to the keyboard: no role, no tab stop,
 * no Enter/Space. Rows that open a detail view this way stranded keyboard
 * users completely — they could tab to the row's Edit and Delete buttons but
 * never open the thing those buttons belonged to.
 *
 * The target guard is the part that is easy to get wrong, and the reason this
 * is shared rather than retyped: these rows nest their own buttons, which
 * handle Enter themselves. Without the guard that keypress also bubbles to the
 * row and opens the detail view on top of the button's own action.
 *
 * A `<button>` wrapper would be the simpler answer, but these rows contain
 * buttons and links of their own, and interactive content inside a button is
 * invalid — the accessibility tree flattens it and the inner controls stop
 * being exposed.
 */
interface ActivatableRowProps {
  role?: 'button'
  tabIndex?: number
  'aria-label'?: string
  onClick?: () => void
  onKeyDown?: (e: KeyboardEvent<HTMLElement>) => void
}

export function activatableRowProps({
  onActivate,
  label,
  enabled = true,
}: {
  onActivate: () => void
  label: string
  /**
   * `false` leaves the row inert — no role, no tab stop, no handlers. For
   * rows that are only sometimes a control (a content row that drills in only
   * when it actually has children); a permanent `tabIndex` there would offer
   * a tab stop that does nothing.
   */
  enabled?: boolean
}): ActivatableRowProps {
  if (!enabled) return {}
  return {
    role: 'button' as const,
    tabIndex: 0,
    'aria-label': label,
    onClick: onActivate,
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      if (e.target !== e.currentTarget) return
      if (e.key !== 'Enter' && e.key !== ' ') return
      e.preventDefault()
      onActivate()
    },
  }
}
